<script setup>
import { computed, inject, onBeforeUnmount, onMounted, provide, ref, useAttrs, watch } from "vue";
import { useVModel } from "@vueuse/core";
import { cva } from "class-variance-authority";
import IMask from "imask";
import { nanoid } from "nanoid";
import { cn } from "~/utils/cn";

defineOptions({ inheritAttrs: false });

const emit = defineEmits(["update:modelValue", "blur", "update:currentValue"]);
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  type: {
    type: String,
    default: "text",
    validator: (v) => ["text", "number", "password", "email", "time", "datetime-local"].includes(v),
  },
  label: { type: String, default: null },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  errors: { type: Array, default: null },
  size: { type: String, validator: (v) => ["sm", "md", "lg"].includes(v) },
  controls: { type: Boolean, default: true },
  mask: { type: [Object, String], default: null },
  loading: { type: Boolean, default: false },
  autocomplete: { type: String, default: "off" },
});

const autocompleteAttrs = computed(() => {
  if (props.autocomplete === "off") {
    return {
      autocomplete: "off",
      "data-1p-ignore": "true",
      "data-lpignore": "true",
      "data-bwignore": "true",
      "data-form-type": "other",
    };
  }
  return { autocomplete: props.autocomplete };
});

const modelValue = useVModel(props, "modelValue", emit, { passive: true });

// styling helpers
const wrapperVariants = cva("flex w-full overflow-hidden rounded-md border shadow-xs ring-1 ring-transparent [&:has(:disabled)]:bg-gray-50", {
  variants: {
    size: { sm: "h-6", md: "h-8", lg: "h-10" },
    state: {
      default: "border-gray-300 bg-white text-gray-900 focus-within:border-blue-500 focus-within:ring-blue-500",
      error: "border-red-500 bg-red-50 text-gray-900 focus-within:border-red-500 focus-within:ring-red-500",
    },
  },
  defaultVariants: { state: "default", size: "md" },
});
const inputVariants = cva("-my-px w-full border-0 bg-transparent text-sm focus:outline-0 focus:ring-0", {
  variants: { size: { sm: "px-2 py-0 leading-6", md: "px-2 py-0 leading-8", lg: "px-3 py-0 leading-10" } },
  defaultVariants: { size: "md" },
});

// render context + size
const renderContext = inject("renderContext", []);
const size = computed(() => {
  if (props.size != null) return props.size;
  if (renderContext.includes("modal")) return "lg";
  if (renderContext.includes("table")) return "sm";
  return "md";
});
provide("INPUT_CONTEXT", { size });

const { class: rootClass, ...inputAttrs } = useAttrs();
const id = computed(() => inputAttrs.id || inputAttrs.name || nanoid());
const name = computed(() => inputAttrs.name ?? id.value);
const maskActive = computed(() => !!props.mask);
const isValid = computed(() => !props.errors?.length);

// ref for the input
const inputRef = ref(null);
let maskInstance = null;

onMounted(() => {
  if (props.mask && inputRef.value) {
    maskInstance = IMask(inputRef.value, typeof props.mask === "string" ? { mask: props.mask } : props.mask);

    // initialize mask value
    if (props.modelValue) {
      maskInstance.value = props.modelValue;
    }

    // sync mask changes to v-model
    maskInstance.on("accept", () => {
      modelValue.value = maskInstance.unmaskedValue;
    });

    // optional: handle complete
    maskInstance.on("complete", () => {
      modelValue.value = maskInstance.unmaskedValue;
    });

    // keep mask in sync if modelValue changes from outside
    watch(modelValue, (newVal) => {
      if (maskInstance && maskInstance.unmaskedValue !== newVal) {
        maskInstance.value = newVal ?? "";
      }
      emit("update:currentValue", maskInstance.unmaskedValue);
    });
  }
});

onBeforeUnmount(() => {
  if (maskInstance) maskInstance.destroy();
});
</script>

<template>
  <div :class="cn(renderContext.includes('form') && 'my-4', rootClass)">
    <BaseLabel v-if="label" :for="id" :required="required" :disabled="disabled" :size="size">
      {{ label }}
    </BaseLabel>
    <div class="flex w-full" :class="wrapperVariants({ size, state: errors?.length ? 'error' : 'default' })">
      <slot name="prefix" />
      <input
        v-if="!maskActive"
        v-bind="{ ...inputAttrs, ...autocompleteAttrs }"
        :id="id"
        ref="inputRef"
        v-model="modelValue"
        :type="type"
        :name="name"
        :class="cn(inputVariants({ size }), controls === false && 'controls-none')"
        :required="required"
        :disabled="disabled"
        :aria-invalid="isValid ? 'false' : 'true'"
        :aria-labelledby="label ? id + 'Label' : null"
        @blur="$emit('blur', $event)"
      />
      <input
        v-else
        v-bind="{ ...inputAttrs, ...autocompleteAttrs }"
        :id="id"
        ref="inputRef"
        :type="type"
        :name="name"
        :class="cn(inputVariants({ size }), controls === false && 'controls-none')"
        :required="required"
        :disabled="disabled"
        :aria-invalid="isValid ? 'false' : 'true'"
        :aria-labelledby="label ? id + 'Label' : null"
        @blur="$emit('blur', $event)"
      />
      <slot name="suffix" />
      <div v-if="loading" class="flex items-center px-3" data-testid="baseInputLoading"><BaseSpinner size="sm" /></div>
    </div>
    <BaseErrorList v-if="errors" :errors="errors" />
  </div>
</template>
